// aiSignalsApi.js
const PROD_API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY_PREFIX = "ai_signal_cache_";
const CACHE_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

/**
 * Get cached AI signal if exists and not expired
 */
export function getCachedAISignal(symbol) {
  try {
    const key = `${CACHE_KEY_PREFIX}${symbol}`;
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { result, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    // If cache is older than 1 hour, invalidate
    if (age > CACHE_EXPIRY_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      result,
      timestamp,
      age,
      expiresIn: CACHE_EXPIRY_MS - age,
    };
  } catch (e) {
    console.warn("Cache retrieval failed:", e);
    return null;
  }
}

/**
 * Store AI signal result in cache
 */
export function cacheAISignal(symbol, result) {
  try {
    const key = `${CACHE_KEY_PREFIX}${symbol}`;
    localStorage.setItem(
      key,
      JSON.stringify({
        result,
        timestamp: Date.now(),
      })
    );
  } catch (e) {
    console.warn("Cache storage failed:", e);
  }
}

/**
 * Clear cache for a symbol
 */
export function clearAISignalCache(symbol) {
  try {
    const key = `${CACHE_KEY_PREFIX}${symbol}`;
    localStorage.removeItem(key);
  } catch (e) {
    console.warn("Cache clear failed:", e);
  }
}

/**
 * Generate AI signal via API
 */
export async function generateAISignal(payload) {
  const res = await fetch(`${PROD_API}/api/ai/signals`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `AI API ${res.status}`);
  }

  const data = await res.json();

  // Auto-cache successful result
  cacheAISignal(payload.symbol, data);

  return data;
}